import { useCallback, useRef, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import type { DocumentProps } from '@react-pdf/renderer';
import { useSnackbar } from 'notistack';
import { registerPdfFonts } from '../fonts';

interface ExportState {
  loading: boolean;
  error: Error | null;
}

/** A document element, or an async builder for documents that need extra
 *  data fetched first — the fetch then shares the loading state and the
 *  error toast with rendering. */
type DocumentSource =
  | React.ReactElement<DocumentProps>
  | (() => Promise<React.ReactElement<DocumentProps>>);

interface ExportResult extends ExportState {
  /** Render + download. Resolves after the browser accepts the blob URL —
   *  true when the file was downloaded, false when it failed (the error is
   *  already shown). A filename function is read once the document is built —
   *  for a name that comes from data the builder fetches. */
  generate: (document: DocumentSource, filename: string | (() => string)) => Promise<boolean>;
  /** Render + open in a new tab (preview mode). Resolves once the tab opens. */
  preview: (document: DocumentSource) => Promise<void>;
}

async function toBlob(source: DocumentSource): Promise<Blob> {
  const element = typeof source === 'function' ? await source() : source;
  return pdf(element).toBlob();
}

/**
 * Generic PDF export hook. Call `generate(<MyDoc {...} />, 'file.pdf')` and
 * the hook handles font registration, Blob creation, file download, and
 * error toasts in one place. Suitable for every PDF trigger in the app.
 *
 * Loading UX: callers read `loading` to disable the button and show a
 * spinner. The hook also pushes a snackbar with a context-friendly label
 * on failure so individual call sites don't have to repeat the try/catch.
 */
export function usePdfExport(): ExportResult {
  const [state, setState] = useState<ExportState>({ loading: false, error: null });
  const { enqueueSnackbar } = useSnackbar();
  const activeUrls = useRef<string[]>([]);

  const run = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      setState({ loading: true, error: null });
      try {
        registerPdfFonts();
        const out = await fn();
        setState({ loading: false, error: null });
        return out;
      } catch (err: any) {
        const e = err instanceof Error ? err : new Error(String(err));
        setState({ loading: false, error: e });
        enqueueSnackbar(`PDF generation failed: ${e.message}`, { variant: 'error' });
        return null;
      }
    },
    [enqueueSnackbar],
  );

  const generate = useCallback<ExportResult['generate']>(
    async (document, filename) => {
      const done = await run(async () => {
        const blob = await toBlob(document);
        const url = URL.createObjectURL(blob);
        activeUrls.current.push(url);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = typeof filename === 'function' ? filename() : filename;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        setTimeout(() => {
          URL.revokeObjectURL(url);
          activeUrls.current = activeUrls.current.filter((u) => u !== url);
        }, 1000);
        return true;
      });
      return done === true;
    },
    [run],
  );

  const preview = useCallback<ExportResult['preview']>(
    async (document) => {
      await run(async () => {
        const blob = await toBlob(document);
        const url = URL.createObjectURL(blob);
        activeUrls.current.push(url);
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) throw new Error('Preview blocked by browser pop-up policy.');
        setTimeout(() => {
          URL.revokeObjectURL(url);
          activeUrls.current = activeUrls.current.filter((u) => u !== url);
        }, 60_000);
        return true;
      });
    },
    [run],
  );

  return { ...state, generate, preview };
}
